import type { FC, PropsWithChildren } from 'react';
import React, { useEffect, useRef } from 'react';

import FakeLink from '../FakeLink';

import type { ClickableProps } from './Clickable.types';
import { useClickableClassNames } from './useClickableClassNames';

const Clickable: FC<PropsWithChildren<ClickableProps>> = ({
  ariaLabel,
  ariaSelected,
  ariaChecked,
  ariaExpanded,
  role,
  title,
  unstyled,
  disabled,
  disallowTabFocus,
  onClick,
  onFocus,
  onHover,
  children,
  display,
  className,
  color,
  colorOverride,
  activeColor,
  focusOutline,
  focusableRef,
}) => {
  const buttonRef = useRef<HTMLButtonElement>(null);
  const tabIndex = disallowTabFocus ? -1 : undefined;

  const classNames = useClickableClassNames({
    display,
    color,
    colorOverride,
    activeColor,
    disabled,
    focusOutline,
    className,
  });

  useEffect(() => {
    if (focusableRef) {
      const focus = () => {
        const button = buttonRef.current;
        // istanbul ignore else
        if (button) {
          button.focus();
        }
      };

      focusableRef({ focus });
    }
  }, [focusableRef]);

  const buttonChildContent = unstyled || disabled ? children : <FakeLink>{children}</FakeLink>;

  return (
    // oxlint-disable-next-line @ms/jsx-forbid-elements
    <button
      className={classNames.clickable}
      aria-label={ariaLabel}
      aria-selected={ariaSelected}
      aria-checked={ariaChecked}
      aria-expanded={ariaExpanded}
      role={role}
      title={title}
      onClick={onClick}
      onFocus={onFocus}
      onMouseEnter={onHover}
      disabled={disabled}
      type="button"
      ref={buttonRef}
      tabIndex={tabIndex}
    >
      {buttonChildContent}
    </button>
  );
};

/**
 * A `Clickable` is an accessible, clickable area that accepts arbitrary children. It is styled
 * like a link by default, but can also be unstyled. Under the hood `Clickable` simply wraps
 * content in a `button` element.
 *
 * @deprecated Choose Fluent 9 Button for standard actions, Fluent 9 Link without href for
 * link-styled actions, UnstyledButton for custom action surfaces, or an anchor/router link
 * for navigation. See the replacement decision table in Clickable/overview.md.
 * Composite FakeLink and custom focus/role usages require consumer-specific review.
 */
export default Clickable;
